
import { isSuperUser, getDeptCodeByEmail } from "../config/roles";
import { getDeptNameByCode } from "../constants/departments";
import type { AppUser } from "../context/AuthContext";

/**
 * Universal Data Visibility Check
 * 
 * Rules:
 * 1. Super Users (KC, AD, Lisa, Irene, IT, King, Ken) can view EVERYTHING.
 * 2. Regular Department Accounts can only view data if:
 *    - They are the "owner" (department match by Name or Code)
 *    - They are explicitly listed in "visibleDepartments" or "coReviewDepts" or "relatedDeptCodes"
 *    - They are the original creator (requester)
 *    - They are assigned to the item
 */
export function canViewData(user: AppUser | null, doc: any): boolean {
  if (!user || !doc) return false;

  // 1. Super User Override
  if (isSuperUser(user.email)) return true;

  // 2. Department Identity
  const userDeptCode = getDeptCodeByEmail(user.email).toUpperCase(); // e.g. "MD"
  const userDeptName = getDeptNameByCode(userDeptCode); // e.g. "行銷設計部"

  // 3. Ownership Checks (String Match)
  
  // Check by Department Name (Legacy & UI fields often use Name)
  if (doc.department === userDeptName) return true;
  if (doc.ourDept === userDeptName) return true;
  if (doc.responsibleDept === userDeptName) return true;
  if (doc.mainDepartment === userDeptName) return true;
  if (doc.applicantDept === userDeptName) return true;
  if (doc.ownerDept === userDeptName) return true; // Compliance Project
  
  // Check by Department Code (System fields often use Code)
  if (doc.primaryDeptCode === userDeptCode) return true;
  if (doc.createdByDeptId === userDeptCode) return true;
  if (doc.ownerDeptCode === userDeptCode) return true; // Compliance Project (New)
  if (doc.assigneeDepartmentCode === userDeptCode) return true; // Generic Assignment

  // Check by User Identity (Requester/Owner sees their own requests)
  if (doc.requesterUid === user.id) return true;
  if (doc.createdBy === user.id) return true;
  if (doc.userId === user.id) return true; // For WorkLogs
  if (doc.ownerUserId === user.id) return true; // Compliance Project Assignee
  if (doc.assigneeUserId === user.id) return true; // Generic Assignment
  if (doc.ownerUser === user.name || doc.ownerUser === user.displayName) return true; // Fallback name match

  // 4. Shared/Authorized Access Checks (Arrays)
  
  // Check visibleDepartments (Name Array)
  if (Array.isArray(doc.visibleDepartments)) {
    if (doc.visibleDepartments.includes(userDeptName)) return true;
    if (doc.visibleDepartments.includes(userDeptCode)) return true; // Just in case
  }

  // Check coReviewDepts (Code Array usually, sometimes Name)
  if (Array.isArray(doc.coReviewDepts)) {
    // Standardize to Upper Case for comparison if codes
    const normalizedCoReview = doc.coReviewDepts.map((d: string) => d.toUpperCase());
    if (normalizedCoReview.includes(userDeptCode)) return true;
  }
  
  // Check assignedDeptCodes (Code Array for Tasks)
  if (Array.isArray(doc.assignedDeptCodes)) {
    if (doc.assignedDeptCodes.includes(userDeptCode)) return true;
  }

  // Check relatedDeptCodes (Code Array for Compliance Projects - New)
  if (Array.isArray(doc.relatedDeptCodes)) {
    const normalizedRelated = doc.relatedDeptCodes.map((d: string) => d.toUpperCase());
    if (normalizedRelated.includes(userDeptCode)) return true;
  }

  // 5. Special Public Flag (For Governance Docs)
  if (doc.visibility === 'public' || doc.isPublic === true) return true;

  return false;
}
